import { createEntityItemId } from '@wix/a11y-rules';
import { useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { rescanTriggerGroupIdAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { ColorContrastRuleFixer } from '../../components/fixers/color-contrast-fixer/color-contrast-rule-fixer';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { STUDIO_COMP_REF_TYPE } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { getPageTypeForBi } from '../shared/bi-helpers';
import { shouldUpdateRuleStatus } from '../shared/helpers';
import { getUpdatedColorContrastElementsFromState } from './get-updated-color-contrast-elements-from-state';
export const useColorContrastRule = () => {
  const {
    t
  } = useTranslation();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const navigateToPage = useNavigation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const setRescanTriggerGroupId = useSetAtom(rescanTriggerGroupIdAtom);
  const {
    getRuleState,
    panelState,
    getGroupData,
    setRuleState
  } = usePanelStateAtom();
  const transformToUiElements = useCallback(elements => elements.map(el => ({
    ...el,
    uiElementId: createEntityItemId([el.elementId], (el == null ? void 0 : el.itemId) ?? '')
  })), []);
  const getColorContrast = useCallback(_ref => {
    var _ruleState$state, _getGroupData;
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const {
      result,
      ruleId,
      metadata
    } = ruleExecutionResult;
    const ruleState = getRuleState(routerId ?? pageId, ruleId);
    const stateElements = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.changedElements) ?? new Map();
    const {
      failedElements: initialFailedElements,
      passedElements: initialPassedElements
    } = result;
    const breakpointId = (metadata == null ? void 0 : metadata.breakpointId) ?? STUDIO_COMP_REF_TYPE;
    const passedElements = [...Array.from(stateElements.values()).filter(el => el.isValid), ...transformToUiElements(initialPassedElements ?? [])];
    const passedElementIds = new Set(passedElements.map(el => el.elementId));
    const filteredInitialFailedElements = (initialFailedElements ?? []).filter(failedElement => !passedElementIds.has(failedElement.elementId));
    const failedElements = transformToUiElements(filteredInitialFailedElements);
    const {
      passedElementsWithState,
      failedElementsWithState
    } = getUpdatedColorContrastElementsFromState([...passedElements, ...failedElements], panelState.globalSectionComponentsDataMap);
    const failedElementCount = (failedElementsWithState == null ? void 0 : failedElementsWithState.length) || 0;
    const elementCount = ((passedElementsWithState == null ? void 0 : passedElementsWithState.length) || 0) + failedElementCount;
    const uiStatus = failedElementsWithState.length === 0 ? RuleListStatus.passed : RuleListStatus.failed;
    const isRuleResolved = uiStatus !== RuleListStatus.failed;
    const analysisState = (_getGroupData = getGroupData(routerId ?? pageId)) == null ? void 0 : _getGroupData.analysisState;
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, ruleId, {
        status: uiStatus
      });
    }
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: isRuleResolved ? failedElementsWithState.length === 0 ? 'accessibility-wizard.rules-list.color-contrast.description.passed' : 'accessibility-wizard.rules-list.color-contrast.description.partial-pass-with-fixed-rules-in-session' : 'accessibility-wizard.rules-list.color-contrast.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-color-contrast-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount,
            failedElementCount
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.color-contrast.description.learn-more.aria-label'),
        title: isRuleResolved ? t('accessibility-wizard.rules-list.issue-resolved') : ''
      }),
      elementCount,
      failedElementCount,
      fixer: FixerType.Custom,
      fixerProps: {
        customFixer: /*#__PURE__*/React.createElement(ColorContrastRuleFixer, {
          breakpointId: breakpointId,
          failedElements: failedElementsWithState,
          pageId: routerId ?? pageId,
          passedElements: passedElementsWithState
        })
      },
      id: `${isCrossPage ? 'cross-page-' : ''}${routerId ?? pageId}-${ruleId}`,
      label: t('accessibility-wizard.rules-list.color-contrast.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount,
          failedElementCount,
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(metadata == null ? void 0 : metadata.itemType, isCrossPage),
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId, routerId);
          setRescanTriggerGroupId(isCrossPage ? pageId : undefined);
        } else {
          setRescanTriggerGroupId(undefined);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount,
          failedElementCount,
          status: uiStatus
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus
    };
  }, [getRuleState, transformToUiElements, panelState.globalSectionComponentsDataMap, getGroupData, t, setRuleState, openSelectedArticle, editorHost, biLogger, setCurrentTaskInfo, currentTaskInfo, navigateToPage, setRescanTriggerGroupId]);
  return {
    getColorContrast
  };
};
//# sourceMappingURL=use-color-contrast-rule.js.map